/**
 * PATH: packages/email-hub/src/feature/pages/inbox-page/controller/useInboxDataRuntime.ts
 * FILE_ID: EMAIL-INBOX-CONTROLLER-DATA-001
 * PURPOSE: Query inbox data, sync state, statistics, and allocation mutations.
 */

/* eslint-disable react-hooks/exhaustive-deps -- Preserve pre-extraction hook dependencies. */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  smartSearchRankParam,
} from '../../../lib/smartSearchFlag'
import {
  useClassifyUnprocessed,
  useEmailActions,
  useEmailJobLink,
  useEmails,
  useEmailStats,
  useEmailSyncStatus,
  useSyncEmails,
  type Email,
} from '../../../hooks/useEmails'
import { useToast } from '../../../../_internal/components/Toast'
import { reconcileLoadedSelection } from '../../../components/inbox-command/contracts'
import { buildExcludeCategories } from '../../../lib/categoryConfig'
import {
  captureListScrollTop,
  restoreListScrollTop,
} from '../preserveListScroll'
import type { useInboxState } from './useInboxState'

type InboxState = ReturnType<typeof useInboxState>

export function useInboxDataRuntime(state: InboxState) {
  const effectiveExcludeCategories = useMemo(() => {
    return buildExcludeCategories(
      state.hidePromotions,
      state.selectedCategories,
      state.mutedCategories,
    )
  }, [
    state.hidePromotions,
    state.selectedCategories,
    state.mutedCategories,
  ])
  const isBinnedFolder = state.folder === 'binned'
  const {
    emails, loading, pendingNewQuery, refreshing, error, hasMore, loadMore,
    refetch, updateEmailLocal, removeEmailsLocal, restoreEmailsLocal,
  } = useEmails({
    folder: state.folder === 'sent'
      ? 'sent'
      : state.isInboxFolder ? 'inbox' : undefined,
    status: state.folder === 'archived'
      ? 'archived'
      : state.folder === 'binned' ? 'binned' : undefined,
    category__in: state.selectedCategories.length
      ? state.selectedCategories
      : undefined,
    // Binned shows EVERYTHING the operator (or Outlook/Gmail) binned. The
    // inbox's promo/muted exclusions and the cleared-card hide would silently
    // drop spam, newsletters and card-linked mail from the bin, which is the
    // one place they must stay findable.
    category__not: isBinnedFolder ? undefined : effectiveExcludeCategories,
    search: state.parsedDebouncedSearch.freeText || undefined,
    search_rank: smartSearchRankParam(state.smartSearchOn),
    is_read:
      state.parsedDebouncedSearch.isReadFilter ?? state.isReadFilter,
    is_starred:
      state.parsedDebouncedSearch.isStarredFilter
      ?? state.isStarredFilter
      ?? (state.folder === 'starred' ? true : undefined),
    allocation: state.allocationFilter,
    hide_cleared:
      !isBinnedFolder
      && state.hideCleared
      && state.allocationFilter !== 'allocated'
        ? true
        : undefined,
    include_archived: state.scopeAll ? true : undefined,
    needs_review: state.needsReviewOnly ? true : undefined,
    job_id: state.jobIdFilter,
    job_id__in: state.jobIdsIn.length > 0 ? state.jobIdsIn : undefined,
    synced_by_account_id__in:
      state.mailboxAccountIds.length > 0
        ? state.mailboxAccountIds
        : undefined,
    unattributed_owner_id__in:
      state.mailboxLegacyUserIds.length > 0
        ? state.mailboxLegacyUserIds
        : undefined,
    from_address__icontains:
      state.parsedDebouncedSearch.fromAddressContains
      ?? (state.fromAddressContains || undefined),
    company_domain: state.companyDomain || undefined,
    received_at__gte:
      state.parsedDebouncedSearch.receivedAtGte ?? state.receivedAtGte,
    received_at__lte:
      state.parsedDebouncedSearch.receivedAtLte ?? state.receivedAtLte,
    has_attachments:
      state.parsedDebouncedSearch.hasAttachments ?? state.hasAttachments,
    has_calendar_event:
      state.parsedDebouncedSearch.hasCalendarEvent ?? state.hasCalendarEvent,
    attachment_filename:
      state.parsedDebouncedSearch.attachmentFilename,
    search_scope: state.parsedDebouncedSearch.searchScope,
    // First-paint budget: titles_only at page_size=200 serialises + paints
    // far more rows than fit the viewport and pushes Newmark cold opens
    // past 2s. 50 covers ~2× a tall monitor; Load older still pages.
    page_size: 50,
    persistKey:
      state.isInboxFolder && state.kanbanSession?.user?.id != null
        ? `u${state.kanbanSession.user.id}:o${state.kanbanSession.organisation?.id ?? 'none'}`
        : undefined,
  })
  const listLoading = loading || pendingNewQuery
  const selectionScopeCheckRef = useRef<
    'idle' | 'awaiting-query' | 'querying'
  >('idle')
  // BUG-2026-07-21-INBOX-SCROLL-RESET-ON-ALLOCATE — mutation refetches must
  // not yank the operator back to the top of a long inbox.
  const pendingScrollRestoreRef = useRef<number | null>(null)
  const refetchPreservingScroll = useCallback(() => {
    pendingScrollRestoreRef.current = captureListScrollTop(state.listRef.current)
    refetch()
  }, [refetch, state.listRef])
  useLayoutEffect(() => {
    const top = pendingScrollRestoreRef.current
    if (top == null) return
    if (loading || pendingNewQuery || refreshing) return
    pendingScrollRestoreRef.current = null
    restoreListScrollTop(state.listRef.current, top)
  }, [
    emails,
    loading,
    pendingNewQuery,
    refreshing,
    state.listRef,
  ])
  // BUG-2026-07-21-INBOX-SCROLL-RESET-ON-ALLOCATE (recurrence). Runtime repro:
  // scrollTop 3000 -> 0 in a single frame with scrollHeight AND row count
  // UNCHANGED, so the reset is an explicit scrollTo(top:0), not a blank/clamp.
  // The trigger is IDENTITY churn of this effect's deps, not a real filter
  // change: `mailboxAccountIds`/`mailboxLegacyUserIds` (via splitMailboxKeys ->
  // effectiveMailboxKeys -> visibleMailboxKeys memoised on `emailAccounts`) and
  // `mutedCategories` are recreated as NEW arrays whenever useEmailAccounts
  // refetches — which an allocate/clear forces (mailbox counts change) and a
  // background poll/sweep forces on its own. Keying the reset on those
  // references made it misfire and yank a mid-scroll operator to the top.
  // Compare filters by VALUE so only a genuine filter/search/mailbox change
  // resets scroll + selection; identity churn from refetches is ignored.
  const filterSignature = JSON.stringify([
    state.selectedCategories,
    state.debouncedSearchQuery,
    state.isReadFilter,
    state.isStarredFilter,
    state.allocationFilter,
    state.jobIdFilter,
    state.jobIdsIn,
    state.fromAddressContains,
    state.companyDomain,
    state.receivedAtGte,
    state.receivedAtLte,
    state.hasAttachments,
    state.hasCalendarEvent,
    state.hideCleared,
    state.hidePromotions,
    state.mutedCategories,
    state.scopeAll,
    // Toggling the AI-review filter is a genuine result-set change, so it must
    // reset scroll and drop the bulk selection like any other filter. Omitting
    // it here would let a selection made in the full inbox survive into the
    // filtered view and carry hidden IDs into a bulk mutation
    // (BUG-2026-07-21-INBOX-SCROLL-RESET-ON-ALLOCATE, same failure family).
    state.needsReviewOnly,
    state.smartSearchOn,
    state.mailboxAccountIds,
    state.mailboxLegacyUserIds,
  ])
  useEffect(() => {
    pendingScrollRestoreRef.current = null
    state.listRef.current?.scrollTo({ top: 0 })
    selectionScopeCheckRef.current = 'awaiting-query'
    // A bulk selection belongs to the exact result set where it was made.
    // Clear it synchronously at the filter boundary so retained query data
    // can never carry hidden IDs into a bulk mutation.
    state.setSelectedIds(new Set())
  }, [filterSignature])
  useEffect(() => {
    if (selectionScopeCheckRef.current === 'idle') return
    if (loading || pendingNewQuery || refreshing) {
      selectionScopeCheckRef.current = 'querying'
      return
    }
    // The filter-change effect runs before useEmails publishes its pending
    // state. Do not reconcile the reader against retained rows from the old
    // query; wait until a real query transition has started and settled.
    if (selectionScopeCheckRef.current !== 'querying') return
    selectionScopeCheckRef.current = 'idle'
    state.setSelectedEmailId((current) =>
      current && !emails.some((email) => email.id === current)
        ? null
        : current,
    )
    state.setSelectedIds((current) =>
      reconcileLoadedSelection(
        current,
        emails.map((email) => email.id),
      ),
    )
  }, [
    emails,
    loading,
    pendingNewQuery,
    refreshing,
    state.setSelectedEmailId,
    state.setSelectedIds,
  ])

  const { data: syncStatus } = useEmailSyncStatus(true)
  const { sync: triggerSync } = useSyncEmails()
  const handleIdentityReload = useCallback(() => {
    try {
      const key = 'qh-inbox-identity-reload-at'
      const last = Number(sessionStorage.getItem(key) || '0')
      if (last && Date.now() - last < 30_000) return
      sessionStorage.setItem(key, String(Date.now()))
    } catch { /* session storage unavailable */ }
    window.location.reload()
  }, [])
  // Keep aligned with the BE active-sync window to avoid healthy-sync alarms.
  const activeSyncAgeSec = syncStatus?.active_sync?.age_seconds ?? null
  const STALL_THRESHOLD_SEC = 300
  const isActiveSyncStalled =
    activeSyncAgeSec !== null && activeSyncAgeSec > STALL_THRESHOLD_SEC
  const isAutoSyncing =
    !!syncStatus?.active_sync && !isActiveSyncStalled
  const {
    stats,
    error: statsError,
    refetch: refetchStats,
  } = useEmailStats()
  const {
    toggleStar, markAsRead, bulkAction, archive, unarchive, toggleRead,
    syncDuplicates,
  } = useEmailActions()
  const { run: runClassify, busy: classifyBusy } = useClassifyUnprocessed()
  const { toast } = useToast()
  const { confirmJob, rejectJob, linkToJob, rejectSuggestion } =
    useEmailJobLink()
  const [jobActionId, setJobActionId] = useState<string | null>(null)
  const handleConfirmJob = useCallback(async (id: string) => {
    setJobActionId(id)
    try {
      await confirmJob(id)
      // BL-1: clear suggestion locally — no full inbox refetch.
      updateEmailLocal(id, { suggested_job: null })
    } finally {
      setJobActionId(null)
    }
  }, [confirmJob, updateEmailLocal])
  const handleRejectJob = useCallback(async (id: string) => {
    setJobActionId(id)
    try {
      await rejectJob(id)
      updateEmailLocal(id, { job_id: null, job_name: null, suggested_job: null })
    } finally {
      setJobActionId(null)
    }
  }, [rejectJob, updateEmailLocal])
  const [allocActionId, setAllocActionId] = useState<string | null>(null)
  const handleConfirmAllocation = useCallback(
    async (emailId: string, allocationId: string) => {
      setAllocActionId(emailId)
      try {
        await state.kanbanApi.confirmAiAllocation(allocationId)
        toast('AI allocation confirmed - added to the board.', 'success')
        // Confirm may change board membership — list row still needs summary;
        // keep refetch only for this path until allocation_summary is patched.
        // Preserve scroll so mid-list confirm doesn't yank to top.
        refetchPreservingScroll()
      } catch (error) {
        toast(
          `Could not confirm: ${(error as Error).message || 'error'}`,
          'error',
        )
      } finally {
        setAllocActionId(null)
      }
    },
    [state.kanbanApi, toast, refetchPreservingScroll],
  )
  const handleRejectAllocation = useCallback(
    async (
      emailId: string,
      allocationId: string,
      priorSummary: Email['allocation_summary'],
    ) => {
      setAllocActionId(emailId)
      updateEmailLocal(emailId, { allocation_summary: null })
      try {
        await state.kanbanApi.rejectAiAllocation(allocationId, {
          reason: 'Operator rejected from inbox frame',
        })
        toast('AI allocation rejected - removed from the board.', 'success')
        // Local patch already cleared the chip — skip list refetch.
      } catch (error) {
        updateEmailLocal(emailId, { allocation_summary: priorSummary })
        toast(
          `Could not reject: ${(error as Error).message || 'error'}`,
          'error',
        )
      } finally {
        setAllocActionId(null)
      }
    },
    [state.kanbanApi, toast, updateEmailLocal],
  )
  const [suggestionActionId, setSuggestionActionId] =
    useState<string | null>(null)

  return {
    emails, loading, pendingNewQuery, refreshing, error, hasMore, loadMore,
    refetch, refetchPreservingScroll, updateEmailLocal, removeEmailsLocal,
    restoreEmailsLocal, listLoading, syncStatus,
    triggerSync, handleIdentityReload, isAutoSyncing, stats, statsError,
    refetchStats, toggleStar, markAsRead, bulkAction, archive, unarchive,
    toggleRead, syncDuplicates, runClassify, classifyBusy, toast, linkToJob,
    rejectSuggestion, jobActionId, handleConfirmJob, handleRejectJob,
    allocActionId, handleConfirmAllocation, handleRejectAllocation,
    suggestionActionId, setSuggestionActionId,
  }
}
